<template>
    <div class="grafico-container">
        <div class="grafico-header">
            <h3 class="grafico-titulo">Pacotes de trabalho atrasados por coordenador</h3>
            <GraficoInfoIcon 
                titulo="Pacotes de trabalho atrasados por coordenador"
                descricao="Mostra a quantidade de pacotes de trabalho atrasados por coordenador, identificando áreas que necessitam de atenção gerencial."
            />
        </div>
        <div v-if="!temDados" class="sem-dados">Nenhum dado disponível</div>
        <Bar v-else :data="chartData" :options="chartOptions" />
    </div>
</template>

<script>
import { Bar } from 'vue-chartjs';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import GraficoInfoIcon from './GraficoInfoIcon.vue';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default {
    name: 'GraficoEntregasPorCoordenador',
    components: { Bar, GraficoInfoIcon },
    props: {
        projetos: {
            type: Array,
            default: () => []
        }
    },
    emits: ['clicar-projeto'],
    computed: {
        dadosPorCoordenador() {
            const coordenadoresMap = {};
            
            this.projetos.forEach(projeto => {
                if (projeto.entregas && Array.isArray(projeto.entregas)) {
                    projeto.entregas.forEach(entrega => {
                        const hoje = new Date();
                        hoje.setHours(0, 0, 0, 0);
                        const dtFim = entrega.dtFim ? new Date(entrega.dtFim) : null;
                        const estaAtrasada = dtFim && dtFim < hoje && entrega.status !== 'Concluído' && entrega.status !== 'Cancelada';
                        
                        if (entrega.coordenador) {
                            const nome = entrega.coordenador.name || 'Sem coordenador';
                            if (!coordenadoresMap[nome]) {
                                coordenadoresMap[nome] = { nome, atrasadas: 0, total: 0 };
                            }
                            coordenadoresMap[nome].total++;
                            if (estaAtrasada) {
                                coordenadoresMap[nome].atrasadas++;
                            }
                        }
                    });
                }
            });
            
            return Object.values(coordenadoresMap)
                .filter((dados) => dados && dados.atrasadas > 0)
                .sort((a, b) => b.atrasadas - a.atrasadas)
                .slice(0, 15);
        },
        temDados() {
            return this.dadosPorCoordenador.length > 0;
        },
        chartData() {
            const dados = this.dadosPorCoordenador;
            return {
                labels: dados.map(d => d.nome.length > 20 ? d.nome.substring(0, 20) + '...' : d.nome),
                datasets: [
                    {
                        label: 'Pacotes de trabalho atrasados',
                        data: dados.map(d => d.atrasadas),
                        backgroundColor: 'rgba(231, 76, 60, 0.6)',
                        borderColor: 'rgba(231, 76, 60, 1)',
                        borderWidth: 2
                    },
                    {
                        label: 'Total de pacotes de trabalho',
                        data: dados.map(d => d.total),
                        backgroundColor: 'rgba(149, 165, 166, 0.4)',
                        borderColor: 'rgba(149, 165, 166, 1)',
                        borderWidth: 2
                    }
                ]
            };
        },
        chartOptions() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                indexAxis: 'y',
                plugins: {
                    legend: {
                        display: true,
                        position: 'top'
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const label = context.dataset.label || '';
                                const value = context.parsed.x;
                                return `${label}: ${value}`;
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        beginAtZero: true,
                        stacked: false,
                        title: {
                            display: true,
                            text: 'Quantidade de pacotes de trabalho'
                        }
                    },
                    y: {
                        stacked: false,
                        title: {
                            display: true,
                            text: 'Coordenadores'
                        }
                    }
                }
            };
        }
    }
};
</script>

<style scoped>
.grafico-container {
    background: var(--cor-bg, white);
    border: 1px solid var(--cor-separador, #ddd);
    border-radius: 8px;
    padding: 1.5rem;
    height: 600px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
}

.grafico-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.grafico-titulo {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--cor-fonte, #333);
}

.sem-dados {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
}
</style>

